import React, { useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Stack, Button, Divider, Paper, Chip, Grid,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import * as XLSX from 'xlsx';
import PopoverFiltroRelatorio from './PopoverFiltroRelatorio';
import generatePdfRelatorioPropostas from './EditPdfRelatorioPropostas';
import { cores, coresGrafico } from '../styles/styleUtils';

// ── Paleta TriAxis — aliases dos tokens de styleUtils ─────────────────────
const LARANJA  = cores.laranja;
const CINZA    = cores.chumbo;
const CINZA2   = cores.cinzaMedio;
const LARANJA2 = cores.laranjaEscuro;
const CORES    = coresGrafico;

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');

// ── KPI Card ───────────────────────────────────────────────────────────────
const KpiCard = ({ label, valor, destaque }) => (
  <Box sx={{
    flex: 1, minWidth: 130, textAlign: 'center',
    border: `1px solid ${destaque ? LARANJA : '#DDD'}`,
    borderRadius: 1, p: 1.5,
    backgroundColor: destaque ? '#FFF3E0' : '#FAFAFA',
    borderLeft: `4px solid ${destaque ? LARANJA : CINZA}`,
  }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="subtitle1" fontWeight="bold" color={destaque ? LARANJA : CINZA}>{valor}</Typography>
  </Box>
);

// ── Título de seção ────────────────────────────────────────────────────────
const SecTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 4 }}>
    <Box sx={{ width: 4, height: 20, backgroundColor: LARANJA, borderRadius: 1 }} />
    <Typography variant="subtitle1" fontWeight="bold" color={CINZA}>{children}</Typography>
  </Box>
);

// ── Tabela de Etapas ───────────────────────────────────────────────────────
const TabelaEtapas = ({ titulo, dados }) => (
  <Box mt={3}>
    {titulo && <Typography variant="body2" fontWeight="bold" color={LARANJA} mb={1}>{titulo}</Typography>}
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
          {['Etapa','Quantidade','Potência (kWp)','Valor (R$)'].map((h) => (
            <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 11 }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {dados.map((e, i) => (
          <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF3E0' } }}>
            <TableCell>{e.etapa}</TableCell>
            <TableCell>{e.quantidade}</TableCell>
            <TableCell>{Number(e.potencia||0).toFixed(2)}</TableCell>
            <TableCell>{fmtBRL(e.valor)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

// ── Tabela de Segmentação ──────────────────────────────────────────────────
const TabelaSegmentacao = ({ titulo, dados }) => (
  <Box mt={3}>
    <Typography variant="body2" fontWeight="bold" color={LARANJA} mb={1}>{titulo}</Typography>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
          {['Grupo','Qtd Total','Potência (kWp)','Valor (R$)','Ticket Médio (R$)','Tx Conversão (%)','Tx Perdidos (%)'].map((h) => (
            <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 11 }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {dados.map((g, i) => (
          <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF3E0' } }}>
            <TableCell>{g.grupo}</TableCell>
            <TableCell>{fmtNum(g.resumo?.qtdTotal || g.tabela?.reduce((s,e) => s+e.quantidade, 0) || 0)}</TableCell>
            <TableCell>{Number(g.resumo?.ticketMedioKwp || 0).toFixed(2)}</TableCell>
            <TableCell>{fmtBRL(g.resumo?.ticketMedioReais || 0)}</TableCell>
            <TableCell>{fmtBRL(g.resumo?.ticketMedioReais || 0)}</TableCell>
            <TableCell>{Number(g.resumo?.taxaConversao || 0).toFixed(1)}%</TableCell>
            <TableCell>{Number(g.resumo?.taxaPerdidos || 0).toFixed(1)}%</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

export default function RelatorioPropostas({ data, type, filtro, setFiltro, onLimpar }) {
  const [openFiltro, setOpenFiltro] = useState(null);

  const onCloseFiltro   = () => setOpenFiltro(null);
  const limparFiltro    = () => { setOpenFiltro(false); onLimpar(); };
  const onConfirmFiltro = (f) => { setOpenFiltro(false); setFiltro(f); };

  if (!data) return null;

  const r = data.resumo;

  // ── Dados para gráficos ────────────────────────────────────────────────
  const dadosQtd = (data.graficoQuantidade || []).filter((d) => d.valor > 0)
    .map((d) => ({ name: d.etapa, value: d.valor }));

  const dadosPot = (data.graficoPotencia || []).filter((d) => d.valor > 0)
    .map((d) => ({ name: d.etapa, value: d.valor }));

  const dadosPorOrigem = (data.porOrigem || []).map((g) => ({
    name: g.grupo,
    quantidade: g.tabela?.reduce((s, e) => s + e.quantidade, 0) || 0,
  }));

  const dadosPorProduto = (data.porProduto || []).map((g) => ({
    name: g.grupo,
    quantidade: g.tabela?.reduce((s, e) => s + e.quantidade, 0) || 0,
  }));

  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    const sheet = [];
    sheet.push(['Relatório de Propostas']);
    sheet.push([]);
    sheet.push(['1. Propostas por Etapas']);
    sheet.push(['Etapa','Quantidade','Potência (kWp)','Valor (R$)']);
    data.tabela.forEach((e) => sheet.push([e.etapa, e.quantidade, e.potencia, e.valor]));
    sheet.push([]);
    sheet.push(['2. Resumo']);
    sheet.push(['Ticket Médio (R$)', r.ticketMedioReais]);
    sheet.push(['Ticket Médio (kWp)', r.ticketMedioKwp]);
    sheet.push(['Taxa de Conversão (%)', r.taxaConversao]);
    sheet.push(['Taxa de Perdidos (%)', r.taxaPerdidos]);
    const ws = XLSX.utils.aoa_to_sheet(sheet);
    XLSX.utils.book_append_sheet(wb, ws, 'Propostas');
    XLSX.writeFile(wb, 'relatorio-propostas.xlsx');
  };

  return (
    <Box mt={4}>
      {/* Barra de ações */}
      <Stack direction="row" justifyContent="flex-end" gap={2} mb={2}>
        <Button variant="outlined" onClick={() => setOpenFiltro(true)}>Filtro</Button>
        <Button variant="outlined" color="secondary" onClick={() => generatePdfRelatorioPropostas(data, filtro)}>Exportar em PDF</Button>
        <Button variant="contained" sx={{ backgroundColor: LARANJA, '&:hover': { backgroundColor: LARANJA2 } }} onClick={exportarExcel}>
          Download Excel
        </Button>
      </Stack>

      {/* Filtros ativos */}
      {filtro?.produto && (
        <Chip label={`Produto: ${filtro.produto}`} size="small" sx={{ mb: 2, backgroundColor: '#FFF3E0', color: LARANJA }} />
      )}

      {/* ── KPIs ─────────────────────────────────────────────────────── */}
      <SecTitle>Resumo de Propostas</SecTitle>
      <Stack direction="row" gap={1.5} flexWrap="wrap" mb={3}>
        <KpiCard label="Ticket Médio (R$)"   valor={fmtBRL(r.ticketMedioReais)}           destaque />
        <KpiCard label="Ticket Médio (kWp)"  valor={`${Number(r.ticketMedioKwp||0).toFixed(2)} kWp`} destaque />
        <KpiCard label="Taxa de Conversão"   valor={`${Number(r.taxaConversao||0).toFixed(1)}%`} />
        <KpiCard label="Taxa de Perdidos"    valor={`${Number(r.taxaPerdidos||0).toFixed(1)}%`} />
      </Stack>

      {/* ── Gráfico 1 — Quantidade por Etapa (pizza) + Potência por Etapa (pizza) */}
      <SecTitle>Distribuição por Etapa</SecTitle>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" color={CINZA2} mb={1} textAlign="center">Quantidade por Etapa</Typography>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={dadosQtd} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                label={({ name, percent }) => `${(percent*100).toFixed(0)}%`} labelLine={false}>
                {dadosQtd.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [v, n]} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" color={CINZA2} mb={1} textAlign="center">Potência por Etapa (kWp)</Typography>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={dadosPot} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                label={({ name, percent }) => `${(percent*100).toFixed(0)}%`} labelLine={false}>
                {dadosPot.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} kWp`]} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </Grid>
      </Grid>

      {/* ── Gráfico 2 — Origem do Atendimento ────────────────────────── */}
      {dadosPorOrigem.length > 0 && (
        <>
          <SecTitle>Propostas por Origem do Atendimento</SecTitle>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dadosPorOrigem} margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="quantidade" name="Propostas" radius={[4,4,0,0]}>
                {dadosPorOrigem.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </>
      )}

      {/* ── Gráfico 3 — Por Produto ───────────────────────────────────── */}
      {dadosPorProduto.length > 0 && (
        <>
          <SecTitle>Propostas por Produto</SecTitle>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dadosPorProduto} margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="quantidade" name="Propostas" radius={[4,4,0,0]}>
                {dadosPorProduto.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </>
      )}

      <Divider sx={{ my: 3 }} />

      {/* ── Tabela principal ──────────────────────────────────────────── */}
      <SecTitle>Propostas por Etapa</SecTitle>
      <TabelaEtapas dados={data.tabela} />

      <Divider sx={{ my: 3 }} />

      {/* ── Segmentações ──────────────────────────────────────────────── */}
      <SecTitle>Segmentações</SecTitle>

      {data.porProduto?.length > 0 && (
        <>
          <Typography variant="body2" fontWeight="bold" color={LARANJA} mt={2} mb={1}>Por Produto</Typography>
          {data.porProduto.map((g, i) => (
            <Box key={i} mt={2} pl={2} borderLeft={`3px solid ${CORES[i % CORES.length]}`}>
              <Typography variant="caption" fontWeight="bold" color={CINZA}>{g.grupo}</Typography>
              <TabelaEtapas dados={g.tabela} />
              <Stack direction="row" gap={1.5} flexWrap="wrap" mt={1}>
                <KpiCard label="Ticket Médio (R$)"  valor={fmtBRL(g.resumo?.ticketMedioReais)} />
                <KpiCard label="Ticket Médio (kWp)" valor={`${Number(g.resumo?.ticketMedioKwp||0).toFixed(2)} kWp`} />
                <KpiCard label="Tx Conversão"       valor={`${Number(g.resumo?.taxaConversao||0).toFixed(1)}%`} />
                <KpiCard label="Tx Perdidos"         valor={`${Number(g.resumo?.taxaPerdidos||0).toFixed(1)}%`} />
              </Stack>
            </Box>
          ))}
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {data.porOrigem?.length > 0 && (
        <>
          <Typography variant="body2" fontWeight="bold" color={LARANJA} mt={2} mb={1}>Por Forma de Atendimento</Typography>
          {data.porOrigem.map((g, i) => (
            <Box key={i} mt={2} pl={2} borderLeft={`3px solid ${CORES[i % CORES.length]}`}>
              <Typography variant="caption" fontWeight="bold" color={CINZA}>{g.grupo}</Typography>
              <TabelaEtapas dados={g.tabela} />
            </Box>
          ))}
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {data.porFabricante?.length > 0 && (
        <>
          <Typography variant="body2" fontWeight="bold" color={LARANJA} mt={2} mb={1}>Por Fabricante</Typography>
          {data.porFabricante.map((g, i) => (
            <Box key={i} mt={2} pl={2} borderLeft={`3px solid ${CORES[i % CORES.length]}`}>
              <Typography variant="caption" fontWeight="bold" color={CINZA}>{g.grupo}</Typography>
              <TabelaEtapas dados={g.tabela} />
            </Box>
          ))}
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {data.porSexo?.length > 0 && (
        <>
          <Typography variant="body2" fontWeight="bold" color={LARANJA} mt={2} mb={1}>Por Sexo do Cliente</Typography>
          {data.porSexo.map((g, i) => (
            <Box key={i} mt={2} pl={2} borderLeft={`3px solid ${CORES[i % CORES.length]}`}>
              <Typography variant="caption" fontWeight="bold" color={CINZA}>{g.grupo}</Typography>
              <TabelaEtapas dados={g.tabela} />
            </Box>
          ))}
        </>
      )}

      {openFiltro && (
        <PopoverFiltroRelatorio filtro={filtro} open={openFiltro}
          onClose={onCloseFiltro} onLimpar={limparFiltro}
          onConfirm={onConfirmFiltro} type={type} />
      )}
    </Box>
  );
}